import { Box, Card, CardContent, FormControl, Grid, Link, Typography, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import { isEqual, isEmpty, split, trim } from 'lodash/fp';
import { Trans, useTranslation } from 'react-i18next';
import sanitizeHtml from 'sanitize-html';
import OutlinedInputField from '../../../components/fields/OutlinedInputField';
import useBasicStyle from '../../../layouts/BasicLayout/useBasicStyles';
import { useHandleError } from '../../../utilities/handleErrors';
import { useStyles } from './CreateSaleTransactionBody';
import CreateSaleTransactionHeader from './CreateSaleTransactionHeader';
import { createDefaultValue, Page, useCreateSalesTransactionState } from './CreateSalesTransactionStateProvider';
import { CreateSaleTransactionFormValues } from '.';

export type VehiclePrefillFormValues = {
    raw: string;
};

const ltaLink = 'https://vrl.lta.gov.sg/lta/vrl/action/pubfunc?ID=EnquireRebateBeforeDeReg';

const parseNumber = (input: string, withDecimal = false) => {
    const purified = input.replace(/[^0-9.-]+/g, '');
    const parsed = withDecimal ? parseFloat(purified) : parseInt(purified, 10);

    return Number.isNaN(parsed) ? null : parsed;
};

const parseDate = (input: string) => {
    const value = dayjs(input);

    return value.isValid() ? value.toDate() : null;
};

const contentToFieldName: Record<string, keyof CreateSaleTransactionFormValues['vehicle']> = {
    'Vehicle No.': 'number',
    'Vehicle Make': 'make',
    'Vehicle Model': 'model',
    'Primary Colour': 'primaryColour',
    'Secondary Colour': 'secondaryColour',
    'Engine No.': 'engineNumber',
    'Motor No.': 'engineNumber',
    'Chassis No.': 'chassisNumber',
    'COE Category': 'coeCategory',
    'OPC Cash Rebate Eligibility': 'opcCashRebateEligibility',
    Messages: 'ltaMessage',
    'Access Code': 'accessCode',

    // boolean
    'Vehicle to be Exported': 'beExported',
    'PARF Eligibility': 'parfEligibility',

    // date
    'Intended Deregistration Date': 'intendedDeregistrationDate',
    'PARF Eligibility Expiry Date': 'parfEligibilityDate',
    'COE Expiry Date': 'coeExpiryDate',
    'OPC Cash Rebate Eligibility Expiry Date': 'opcCashRebateEligibilityExpiryDate',
    'Original Registration Date': 'originalRegistrationDate',
    'First Registration Date': 'firstRegistrationDate',

    // int
    'Manufacturing Year': 'manufacturingYear',
    'Transfer Count': 'transferCount',
    'COE Period (Years)': 'coePeriodYear',

    // sanitizeHtml
    'Maximum Power Output': 'maximumPowerOutput',

    // float
    'Open Market Value': 'openMarketValue',
    'Actual ARF Paid': 'actualARFPaid',
    'PARF Rebate Amount': 'parfRebateAmount',
    'QP Paid': 'qpPaid',
    'COE Rebate Amount': 'coeRebateAmount',
    'OPC Cash Rebate Amount': 'opcCashRebateAmount',
    'PQP Paid': 'pqpPaid',
    'Total Rebate Amount': 'totalRebateAmount',
};

// longer labels are matched first so that e.g. "PARF Eligibility Expiry Date" wins over "PARF Eligibility".
const fieldNameMatchers = Object.entries(contentToFieldName)
    .map(([content, fieldName]) => ({ content: content.toLowerCase(), fieldName }))
    .sort((a, b) => b.content.length - a.content.length);

const matchFieldName = (line: string) =>
    fieldNameMatchers.find(({ content }) => line.toLowerCase().startsWith(content));

// certain values are copied as numbered lists on their own line, e.g. "1. MTR00001" under "Motor no."
const stripListIndex = (value: string) => trim(value.replace(/^\d+\.\s*/, ''));

const assignVehicleField = (
    vehicle: CreateSaleTransactionFormValues['vehicle'],
    fieldName: keyof CreateSaleTransactionFormValues['vehicle'],
    value: string
) => {
    switch (fieldName) {
        case 'number':
        case 'make':
        case 'model':
        case 'primaryColour':
        case 'secondaryColour':
        case 'chassisNumber':
        case 'opcCashRebateEligibility':
        case 'ltaMessage':
        case 'accessCode':
            vehicle[fieldName] = value;
            break;

        case 'coeCategory':
            // The copied text may include the "Details at OneMotoring" link shown next to the value
            vehicle[fieldName] = trim(value.replace(/-?\s*Details at OneMotoring\s*$/i, ''));
            break;

        case 'engineNumber':
            if (!isEmpty(value)) {
                vehicle[fieldName] = value;
            }
            break;

        case 'beExported':
        case 'parfEligibility':
            vehicle[fieldName] = value.toLowerCase().includes('yes');
            break;

        case 'intendedDeregistrationDate':
        case 'originalRegistrationDate':
        case 'firstRegistrationDate':
        case 'parfEligibilityDate':
        case 'coeExpiryDate':
        case 'opcCashRebateEligibilityExpiryDate':
            vehicle[fieldName] = parseDate(value);
            break;

        case 'manufacturingYear':
        case 'transferCount':
        case 'coePeriodYear':
            vehicle[fieldName] = parseNumber(value);
            break;

        case 'maximumPowerOutput':
            vehicle[fieldName] = sanitizeHtml(value).replace(/[\t\n]/g, '');
            break;

        case 'openMarketValue':
        case 'actualARFPaid':
        case 'parfRebateAmount':
        case 'qpPaid':
        case 'coeRebateAmount':
        case 'opcCashRebateAmount':
        case 'pqpPaid':
        case 'totalRebateAmount':
            vehicle[fieldName] = parseNumber(value, true);
            break;

        default:
            break;
    }
};

const parsePrefilledFields = (raw: string | null | undefined, currentValue: CreateSaleTransactionFormValues) => {
    if (isEmpty(raw)) {
        return {
            ...currentValue,
            vehicle: createDefaultValue().vehicle,
        };
    }

    // blank lines are kept as boundries, used mainly for "Messages"
    const lines = split(/\r?\n/, raw).map(trim);

    const result: CreateSaleTransactionFormValues = {
        ...currentValue,
        vehicle: {
            ...createDefaultValue().vehicle,
            ...currentValue.vehicle,
        },
    };

    const skipBlankLines = (index: number) => {
        let nextIndex = index;
        while (nextIndex < lines.length && isEmpty(lines[nextIndex])) {
            nextIndex += 1;
        }

        return nextIndex;
    };

    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index];
        const matcher = matchFieldName(line);

        // skip unrecognized lines
        if (!matcher) {
            continue;
        }

        // format: {key}{value}
        let value = stripListIndex(line.slice(matcher.content.length));

        // "Messages" may span for few lines
        if (matcher.fieldName === 'ltaMessage') {
            const messageLines = isEmpty(value) ? [] : [value];
            // there can be blank lines after "Messages" label and before contents
            let nextIndex = skipBlankLines(index + 1);
            while (nextIndex < lines.length && !matchFieldName(lines[nextIndex])) {
                messageLines.push(lines[nextIndex]);
                nextIndex += 1;
                // "Messages" terminates when we reach next blank line
                if (skipBlankLines(nextIndex) !== nextIndex) {
                    break;
                }
            }
            index = nextIndex - 1;
            assignVehicleField(result.vehicle, matcher.fieldName, messageLines.join('\n'));
            continue;
        }

        // certain fields have its value on next line, e.g. "Motor no."/"Battery no."
        if (isEmpty(value)) {
            const valueIndex = skipBlankLines(index + 1);
            if (valueIndex < lines.length && !matchFieldName(lines[valueIndex])) {
                value = stripListIndex(lines[valueIndex]);
                index = valueIndex;
            }
        }

        assignVehicleField(result.vehicle, matcher.fieldName, value);
    }

    return result;
};

const VehiclePrefillForm = () => {
    const styles = useStyles();
    const theme = useTheme();
    const basicStyles = useBasicStyle();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    useTranslation(['saleTransactionsPage']);

    const {
        state: { prefillRaw, formValue },
        actions,
    } = useCreateSalesTransactionState();

    const onSubmit = useHandleError(async ({ raw }: VehiclePrefillFormValues) => {
        const nextFormValue = parsePrefilledFields(raw, formValue);
        const hasVehicleChanged = !isEqual(formValue.vehicle, nextFormValue.vehicle);

        if (hasVehicleChanged) {
            // Reset TradeIn details if vehicle details have been changed to prevent stale trade-in values
            actions.setFormValue({
                ...createDefaultValue(),
                vehicle: {
                    ...nextFormValue.vehicle,
                },
            });
        } else {
            actions.setFormValue(nextFormValue);
        }

        actions.setPrefillRaw(raw);
        actions.setCurrentPage(Page.VehicleForm);
    }, []);

    return (
        <Formik initialValues={{ raw: prefillRaw }} onSubmit={onSubmit}>
            {() => (
                <Form>
                    <CreateSaleTransactionHeader currentPage={Page.VehiclePrefill} />
                    <Box className={basicStyles.mainDesktop}>
                        <Box className={styles.root} mb={isSmall ? 10 : 0}>
                            <Grid container>
                                <Grid xs={12} item>
                                    <Card>
                                        <CardContent>
                                            <Box mb={2}>
                                                <Typography>
                                                    <Trans i18nKey="saleTransactionsPage:prefill.note">
                                                        <Link
                                                            color="secondary"
                                                            href={ltaLink}
                                                            target="_blank"
                                                            underline="always"
                                                        >
                                                            link
                                                        </Link>
                                                    </Trans>
                                                </Typography>
                                            </Box>
                                            <Grid spacing={2} container>
                                                <Grid lg={4} md={6} xs={12} item>
                                                    <FormControl fullWidth>
                                                        <OutlinedInputField name="raw" rows={10} multiline />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    );
};

export default VehiclePrefillForm;
